<template>
  <div>
    <div class="form-group row">
      <div class="col-md-8">
        <label style="float: left;">Contact Name</label>
        <el-input type="search"
               name="search"
               placeholder="Search"
               clearable
               @keyup.enter.native="searchSubmit"
               v-validate="'required'"
               v-model="query.search" />
        <small class="text-danger" v-show="errors.has('search')">
          {{ getError('search') }}
        </small>
      </div>
    </div>
    <div class="form-group row">
      <div class="col-md-12 switch-button">
        <div class="switch">
          <label class="switch-label">Show Inactive</label>
          <p-switch v-model="showInactive" type="primary" on-text="YES" off-text="NO" class="pull-right" style="display:inline"></p-switch>
        </div>
        <button type="button" :disabled="isClearButtonDisabled" class="btn btn-fill btn-info" @click="clear">
          Clear
        </button>
        <button type="button" :disabled="isSearchButtonDisabled" class="btn btn-fill btn-info" @click="searchSubmit">
          Search
        </button>
      </div>
    </div>

    <div v-if="showInactive" class="pull-left" style="margin-bottom: 5px">
      <strong>LEGEND: </strong><span class="warning-row">Orange rows indicate <u>inactive</u> contacts.</span>
    </div>
    <el-table class="wrap-whole-words table-striped indexed-table-column-width"
              :row-class-name="inactiveContact"
              :data="displayedContacts"
              border
              style="width: 100%"
              :max-height="tableHeight(500)">
      <el-table-column
        :width="indexColumnWidth"
        align="center"
        label="#"
        type="index">
      </el-table-column>
      <el-table-column
        min-width="200"
        :sortable="true"
        :sort-method="(a, b) => callStringSort(a.contact, b.contact, 'name')"
        label="Name">
        <template slot-scope="props">
          <span class="invisible">{{props.row.contact.id}}</span>
          <a href="#" @click.prevent="editContact(props.row)">{{props.row.contact.name}}</a>
        </template>
      </el-table-column>
      <el-table-column
        min-width="200"
        :sortable="true"
        sort-by="_source.companyName"
        label="Company">
        <template slot-scope="props">
          <span class="invisible">{{props.row.companyId}}</span>
          <a href="#" @click.prevent="editCompany(props.row)">{{props.row.companyName}}</a>
        </template>
      </el-table-column>
      <el-table-column
          min-width="100"
          label="Title">
        <template slot-scope="props">
          {{props.row.contact.title }}
        </template>
      </el-table-column>
      <el-table-column
          min-width="150"
          label="Work Phone">
        <template slot-scope="props">
          {{props.row.contact.workPhone }}
        </template>
      </el-table-column>
      <el-table-column
          min-width="150"
          label="Mobile Phone">
        <template slot-scope="props">
          {{props.row.contact.mobilePhone }}
        </template>
      </el-table-column>
      <el-table-column
          min-width="200"
          label="E-Mail(s)">
        <template slot-scope="props">
          {{ props.row.contact.emails | toCsv }}
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
  import {mapGetters} from 'vuex'
  import {getTableHeight, notifyError, notifyWarning, setIndexColumnWidth, stringSort} from 'src/utilities/utils'
  import Constants from 'src/utilities/constants'
  import {OK} from 'http-status-codes'

  export default {
    computed: {
      ...mapGetters({
        tableData: 'companies/contactsResults'
      }),
      isSearchButtonDisabled() {
        return !this.query.search
      },
      isClearButtonDisabled() {
        return !this.query.search && !this.tableData.length
      },
      indexColumnWidth() {
        return setIndexColumnWidth(this.tableData.length)
      },
      displayedContacts() {
        if (this.showInactive) {
          return this.tableData
        }
        return this.tableData.filter(company => company.contact.active === true)
      }
    },
    data() {
      return {
        constants: Constants,
        showInactive: false,
        query: {
          search: null,
          types: [],
          active: true
        }
      }
    },
    methods: {
      callStringSort(a, b, prop) {
        return stringSort(a, b, prop)
      },
      clear() {
        this.$validator.pause()
        this.query = {
          search: "",
          types: [],
          active: true
        }
        this.$store.commit('companies/resetContactsResults')
        setTimeout(this.resumeValidation, 250)
      },
      inactiveContact({row}) {
        if (!row.contact.active) {
          return 'warning-row'
        }
      },
      resumeValidation() {
        this.$validator.resume()
      },
      getError(fieldName) {
        return this.errors.first(fieldName)
      },
      async searchSubmit() {
        if (await this.$validator.validateAll()) {
          const response = await this.$store.dispatch('companies/fetchContacts', this.query.search.trim())

          if (OK !== response.status) {
            notifyError(this, 'Failed to execute contacts search')
            return
          }

          if (response.data.length === 0) {
            this.$eventHub.$emit('show-modal-1', 'No Results Found', null, '500px')
          }
        }
      },
      tableHeight(buffer) {
        return getTableHeight(buffer)
      },
      editCompany(row) {
        const company = {
          name: row.companyName,
          id: row.companyId
        }

        this.$store.commit('companies/resetEdit')
        this.$eventHub.$emit('show-modal-2', 'Company', company, '1000px')
      },
      async editContact(row) {
        const response = await this.$store.dispatch('fetchById', {
          endpoint: 'companies',
          id: row.companyId
        })

        if (OK !== response.status) {
          notifyWarning(this, 'Failed to load Company record')
          return
        }

        this.$store.commit('companies/hydrateEdit', response.data)

        this.$eventHub.$emit('show-modal-5', 'Company Contact', row.contact, '800px')
      },
      updateContactResult(contact) {
        this.$store.set('companies/updateContactResult', contact)
      }
    },
    mounted() {
      this.clear()
      this.$eventHub.$on('companyContactUpdated', this.updateContactResult)
    },
    destroyed() {
      this.$eventHub.$off('companyContactUpdated')
    }
  }
</script>

<style scoped>
  .switch-button {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }
  .switch {
    margin-top: 4px;
    display: flex;
    align-items: center;
    margin-right: 5px;
  }
  .switch-label {
    display:inline;
    margin-right: 3px;
  }
</style>
